/**
 * 草图缩放管理器。
 * 职责：
 * 1. 监听草图选中状态，渲染缩放框与 8 个缩放手柄；
 * 2. 管理缩放手柄拖拽生命周期（开始、更新、结束）；
 * 3. 在拖拽结束后提交统一草图定义变更，由现有重建协调器消费。
 */

import * as THREE from 'three/webgpu';
import { SceneAuxiliaryObjectRegistry } from '../../../scene-object';
import {shapeCircle} from 'three/tsl';
import type {Viewer, ViewerMode} from '../../Viewer';
import {type InteractionModeChangePayload} from '../../../manager/InteractionStateManager';
import {SKETCH_SCALE_ENTITIES_COMMIT_REASON} from '../../../sketch/SketchDefinitionCommitReasons';
import {
    isAbstractSketchEdgeObject,
    isSketchPointObject,
    type AbstractSketchEdgeObject,
    type SketchGroup,
    SketchPointBoxSelectionRole,
    SketchPointKind,
    type SketchPointObject,
} from '../../../mesh/sketch';
import {
    SketchScaleCursor,
    type SketchBounds2D,
    type SketchPlaneBasis,
    type SketchPointSnapshot,
    SketchScaleDebugOverlayMode,
    type SketchScaleDragState,
    SketchScaleHandleId,
} from './SketchScaleTypes';
import {CameraSpriteNodeMaterial} from '../../../material/CameraSpriteNodeMaterial';
import {
    ScreenSpaceDashedLineNodeMaterial,
    SketchLineMaterialDepthPolicy,
    syncScreenSpaceDashedLineGeometry,
} from '../../../material';
export { SketchScaleDebugOverlayMode } from './SketchScaleTypes';


/**
 * 草图缩放边界计算结果。
 */
interface SketchScaleBoundsCalculationResult {
    /** 二维边界。 */
    bounds: SketchBounds2D;
    /** 参与边界计算的世界点。 */
    sourceWorldPoints: THREE.Vector3[];
}


export class SketchScaleController {
    
    /**
     * 调试开关。
     * 说明：可在控制台通过 `window.viewer.sketchGroupManager.sketchScaleController.setDebugOverlayMode('bounds')`
     * 打开缩放边界计算可视化。
     */
    public static readonly debug = {
        /** 调试叠加层模式。 */
        overlayMode: SketchScaleDebugOverlayMode.Off,
    };
    

    /** 最小缩放倍率。 */
    private static readonly MIN_SCALE = 0.01;
    /** 最大缩放倍率。 */
    private static readonly MAX_SCALE = 100;
    
    /** 缩放框虚线段长度（屏幕空间像素语义）。 */
    private static readonly BOUNDS_DASH_SIZE = 7;
    /** 缩放框虚线间隔长度（屏幕空间像素语义）。 */
    private static readonly BOUNDS_GAP_SIZE = 4;
    
    /** 缩放控件控制点固定缩放值。 */
    private static readonly HANDLE_CONTROL_POINT_SCALE = 0.01;
    /** 缩放控制框外扩比例（相对草图边界对角线）。 */
    private static readonly CONTROL_BOUNDS_PADDING_RATIO = 0.04;
    /** 缩放框颜色。 */
    private static readonly BOUNDS_COLOR = 0x8ab4ff;
    /** 手柄颜色。 */
    private static readonly HANDLE_COLOR = 0x4aa3ff;
    
    /** 调试边界颜色。 */
    private static readonly DEBUG_BOUNDS_COLOR = 0xff8a00;
    /** 调试参与计算点颜色。 */
    private static readonly DEBUG_SOURCE_POINT_COLOR = 0xffd000;
    /** 调试手柄落点颜色。 */
    private static readonly DEBUG_HANDLE_POINT_COLOR = 0x22c55e;
    /** 调试点屏幕尺寸。 */
    private static readonly DEBUG_POINT_SIZE = 5;
    
    /** 选中草图组。 */
    private selectedSketchGroup: SketchGroup | null = null;
    /** 是否已启动。 */
    private isStarted = false;
    /** 叠加层根节点。 */
    private overlayRoot: THREE.Group | null = null;
    /** 包围框线对象。 */
    private boundsLine: THREE.Line<THREE.BufferGeometry, ScreenSpaceDashedLineNodeMaterial> | null = null;
    
    /** 手柄精灵对象映射。 */
    private readonly handleSpriteMap = new Map<SketchScaleHandleId, THREE.Sprite>();
    /** 手柄精灵材质。 */
    private handleMaterial: CameraSpriteNodeMaterial | null = null;
    
    
    /** 调试叠加层根节点。 */
    private debugOverlayRoot: THREE.Group | null = null;
    /** 调试边界线。 */
    private debugBoundsLine: THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial> | null = null;
    /** 调试参与计算点集合。 */
    private debugSourcePoints: THREE.Points<THREE.BufferGeometry, THREE.PointsMaterial> | null = null;
    /** 调试手柄落点集合。 */
    private debugHandlePoints: THREE.Points<THREE.BufferGeometry, THREE.PointsMaterial> | null = null;
    
    /** 对象 UUID 到手柄 ID 的映射。 */
    private readonly handleIdByObjectId = new Map<string, SketchScaleHandleId>();
    /** 射线投射器。 */
    private readonly raycaster = new THREE.Raycaster();
    /** 当前 NDC 坐标。 */
    private readonly mouseNdc = new THREE.Vector2();
    /** 当前拖拽状态。 */
    private dragState: SketchScaleDragState | null = null;
    /** 当前游标样式。 */
    private cursor: SketchScaleCursor = SketchScaleCursor.Default;
    /** 交互模式变化监听器。 */
    private readonly modeChangeListener = (_payload: InteractionModeChangePayload): void => {
        this.refreshOverlay();
    };
    /** 鼠标按下事件处理器（捕获阶段）。 */
    private readonly handleMouseDownCapture = (event: MouseEvent): void => {
        this.beginScaleDrag(event);
    };
    /** 鼠标移动事件处理器（捕获阶段）。 */
    private readonly handleMouseMoveCapture = (event: MouseEvent): void => {
        if (this.dragState) {
            this.updateScaleDrag(event);
            return;
        }
        this.updateHandleHoverCursor(event);
    };
    /** 鼠标抬起事件处理器（捕获阶段）。 */
    private readonly handleMouseUpCapture = (): void => {
        this.endScaleDrag();
    };

    /**
     * 构造函数。
     * @param viewer 视口实例。
     */
    constructor(private readonly viewer: Viewer) {}

    /**
     * 判断当前是否已启用草图缩放。
     * @returns 当存在激活草图时返回 true。
     */
    public hasActiveSketchGroup(): boolean {
        return this.selectedSketchGroup !== null;
    }

    
    /**
     * 设置草图缩放调试叠加层模式。
     * @param mode 调试叠加层模式。
     */
    public setDebugOverlayMode(mode: SketchScaleDebugOverlayMode): void {
        SketchScaleController.debug.overlayMode = mode;
        this.refreshOverlay();
    }
    

    /**
     * 启用指定草图组的缩放控制。
     * @param sketchGroup 草图组对象。
     */
    public enableSketchGroup(sketchGroup: SketchGroup): void {
        this.selectedSketchGroup = sketchGroup;
        this.refreshOverlay();
    }

    /**
     * 关闭当前草图缩放控制。
     * 说明：
     * 1. 若当前存在拖拽，会先回滚拖拽快照；
     * 2. 同时恢复视口交互与游标；
     * 3. 最后隐藏缩放叠加层。
     */
    public disableSketchGroup(): void {
        if (this.dragState) {
            this.rollbackSnapshots(this.dragState);
            this.dragState = null;
        }
        this.selectedSketchGroup = null;
        this.viewer.setControlsEnabled(true);
        this.setCursor(SketchScaleCursor.Default);
        this.refreshOverlay();
    }

    /**
     * 启动管理器。
     */
    public start(): void {
        if (this.isStarted) {
            return;
        }
        if (!this.viewer.container) {
            throw new Error('草图缩放管理器启动失败：Viewer 依赖未就绪。');
        }
        this.createOverlayObjects();
        this.bindPointerEvents();
        this.viewer.interactionStateManager?.onModeChange(this.modeChangeListener);
        this.isStarted = true;
    }

    /**
     * 停止管理器。
     */
    public stop(): void {
        if (!this.isStarted) {
            return;
        }
        this.unbindPointerEvents();
        this.viewer.interactionStateManager?.offModeChange(this.modeChangeListener);
        this.dragState = null;
        this.selectedSketchGroup = null;
        this.removeOverlayObjects();
        this.setCursor(SketchScaleCursor.Default);
        this.viewer.setControlsEnabled(true);
        this.isStarted = false;
    }

    /**
     * 释放资源。
     */
    public dispose(): void {
        this.stop();
    }

    /**
     * 绑定鼠标事件。
     */
    private bindPointerEvents(): void {
        const container = this.viewer.container;
        if (!container) {
            throw new Error('草图缩放管理器绑定失败：容器不存在。');
        }
        container.addEventListener('mousedown', this.handleMouseDownCapture, true);
        container.addEventListener('mousemove', this.handleMouseMoveCapture, true);
        window.addEventListener('mouseup', this.handleMouseUpCapture, true);
    }

    /**
     * 解绑鼠标事件。
     */
    private unbindPointerEvents(): void {
        const container = this.viewer.container;
        if (!container) {
            return;
        }
        container.removeEventListener('mousedown', this.handleMouseDownCapture, true);
        container.removeEventListener('mousemove', this.handleMouseMoveCapture, true);
        window.removeEventListener('mouseup', this.handleMouseUpCapture, true);
    }

    /**
     * 创建缩放叠加层对象。
     */
    private createOverlayObjects(): void {
        if (this.overlayRoot) {
            return;
        }
        const overlayRoot = new THREE.Group();
        overlayRoot.name = 'SketchScaleOverlayRoot';
        // 缩放框、八个手柄和调试子树继承辅助身份，不影响原草图与手柄射线检测。
        SceneAuxiliaryObjectRegistry.markRoot(overlayRoot);
        const boundsGeometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(),
            new THREE.Vector3(),
            new THREE.Vector3(),
            new THREE.Vector3(),
            new THREE.Vector3(),
        ]);
        
        const boundsMaterial = SketchLineMaterialDepthPolicy.apply(new ScreenSpaceDashedLineNodeMaterial({
            color: SketchScaleController.BOUNDS_COLOR,
            dashSize: SketchScaleController.BOUNDS_DASH_SIZE,
            gapSize: SketchScaleController.BOUNDS_GAP_SIZE,
            transparent: true,
            opacity: 0.95,
        }));
        
        const boundsLine = new THREE.Line(boundsGeometry, boundsMaterial);
        syncScreenSpaceDashedLineGeometry(boundsLine);
        boundsLine.renderOrder = 8000;
        overlayRoot.add(boundsLine);

        const handleMaterial = this.createScaleHandleMaterial();
        Object.values(SketchScaleHandleId).forEach((handleId) => {
            const handleSprite = new THREE.Sprite(handleMaterial);
            handleSprite.name = `SketchScaleHandle-${handleId}`;
            handleSprite.renderOrder = 8100;
            overlayRoot.add(handleSprite);
            this.handleSpriteMap.set(handleId, handleSprite);
            this.handleIdByObjectId.set(handleSprite.uuid, handleId);
        });

        this.handleMaterial = handleMaterial;
        this.createDebugOverlayObjects(overlayRoot);
        overlayRoot.visible = false;
        this.viewer.addObject(overlayRoot);
        this.overlayRoot = overlayRoot;
        this.boundsLine = boundsLine;
    }

    
    /**
     * 创建缩放手柄材质。
     * @returns 相机感知的手柄精灵材质。
     */
    private createScaleHandleMaterial(): CameraSpriteNodeMaterial {
        const material = new CameraSpriteNodeMaterial({
            sizeAttenuation: false,
            alphaToCoverage: true,
            transparent: true,
            opacityNode: shapeCircle(),
            color: SketchScaleController.HANDLE_COLOR,
        });
        material.name = '草图缩放手柄材质';
        material.opacity = 0.9;
        material.depthTest = false;
        material.depthWrite = false;
        return material;
    }
    

    
    /**
     * 创建缩放边界调试叠加层对象。
     *
     * @param overlayRoot 缩放叠加层根节点。
     */
    private createDebugOverlayObjects(overlayRoot: THREE.Group): void {
        const debugOverlayRoot = new THREE.Group();
        debugOverlayRoot.name = 'SketchScaleBoundsDebugRoot';

        const debugBoundsLine = new THREE.Line(
            new THREE.BufferGeometry().setFromPoints([
                new THREE.Vector3(),
                new THREE.Vector3(),
                new THREE.Vector3(),
                new THREE.Vector3(),
                new THREE.Vector3(),
            ]),
            
            SketchLineMaterialDepthPolicy.apply(new THREE.LineBasicMaterial({
                color: SketchScaleController.DEBUG_BOUNDS_COLOR,
                transparent: true,
                opacity: 0.95,
            })),
            
        );
        debugBoundsLine.name = 'SketchScaleDebugBoundsLine';
        debugBoundsLine.renderOrder = 8200;
        debugOverlayRoot.add(debugBoundsLine);

        const debugSourcePoints = new THREE.Points(
            new THREE.BufferGeometry(),
            new THREE.PointsMaterial({
                color: SketchScaleController.DEBUG_SOURCE_POINT_COLOR,
                size: SketchScaleController.DEBUG_POINT_SIZE,
                sizeAttenuation: false,
                transparent: true,
                opacity: 0.95,
                depthTest: false,
            }),
        );
        debugSourcePoints.name = 'SketchScaleDebugSourcePoints';
        debugSourcePoints.renderOrder = 8210;
        debugOverlayRoot.add(debugSourcePoints);

        const debugHandlePoints = new THREE.Points(
            new THREE.BufferGeometry(),
            new THREE.PointsMaterial({
                color: SketchScaleController.DEBUG_HANDLE_POINT_COLOR,
                size: SketchScaleController.DEBUG_POINT_SIZE + 2,
                sizeAttenuation: false,
                transparent: true,
                opacity: 0.95,
                depthTest: false,
            }),
        );
        debugHandlePoints.name = 'SketchScaleDebugHandlePoints';
        debugHandlePoints.renderOrder = 8220;
        debugOverlayRoot.add(debugHandlePoints);

        debugOverlayRoot.visible = false;
        overlayRoot.add(debugOverlayRoot);
        this.debugOverlayRoot = debugOverlayRoot;
        this.debugBoundsLine = debugBoundsLine;
        this.debugSourcePoints = debugSourcePoints;
        this.debugHandlePoints = debugHandlePoints;
    }
    

    /**
     * 移除缩放叠加层对象。
     */
    private removeOverlayObjects(): void {
        if (!this.overlayRoot) {
            return;
        }
        if (this.overlayRoot.parent) {
            this.overlayRoot.parent.remove(this.overlayRoot);
        }
        this.disposeDebugOverlayObjects();
        this.boundsLine?.geometry.dispose();
        this.boundsLine?.material.dispose();
        this.handleSpriteMap.clear();
        this.handleMaterial?.dispose();
        this.handleMaterial = null;
        this.handleIdByObjectId.clear();
        this.boundsLine = null;
        this.overlayRoot = null;
    }

    
    /**
     * 释放缩放边界调试叠加层资源。
     */
    private disposeDebugOverlayObjects(): void {
        this.debugBoundsLine?.geometry.dispose();
        this.debugBoundsLine?.material.dispose();
        this.debugSourcePoints?.geometry.dispose();
        this.debugSourcePoints?.material.dispose();
        this.debugHandlePoints?.geometry.dispose();
        this.debugHandlePoints?.material.dispose();
        this.debugOverlayRoot = null;
        this.debugBoundsLine = null;
        this.debugSourcePoints = null;
        this.debugHandlePoints = null;
    }
    

    /**
     * 刷新缩放叠加层显示。
     */
    private refreshOverlay(): void {
        if (!this.overlayRoot || !this.boundsLine) {
            return;
        }
        if (this.viewer.getMode() === ('sketch-drawing' as ViewerMode)) {
            this.overlayRoot.visible = false;
            this.hideDebugOverlay();
            // 叠加层显隐只产生普通视图脏状态，交给统一帧调度器合并。
            this.viewer.requestRender();
            return;
        }
        if (!this.selectedSketchGroup) {
            this.overlayRoot.visible = false;
            this.hideDebugOverlay();
            // 叠加层显隐只产生普通视图脏状态，交给统一帧调度器合并。
            this.viewer.requestRender();
            return;
        }
        const basis = this.getSketchPlaneBasis(this.selectedSketchGroup);
        const boundsResult = this.computeSketchBoundsCalculationResult(this.selectedSketchGroup, basis);
        const {bounds} = boundsResult;
        const controlBounds = this.expandBoundsForScaleControls(bounds);
        const handleUvMap = this.resolveHandleUvMap(controlBounds);
        const cornersWorld = [
            this.planeToWorld(new THREE.Vector2(controlBounds.minX, controlBounds.minY), basis),
            this.planeToWorld(new THREE.Vector2(controlBounds.maxX, controlBounds.minY), basis),
            this.planeToWorld(new THREE.Vector2(controlBounds.maxX, controlBounds.maxY), basis),
            this.planeToWorld(new THREE.Vector2(controlBounds.minX, controlBounds.maxY), basis),
            this.planeToWorld(new THREE.Vector2(controlBounds.minX, controlBounds.minY), basis),
        ];
        this.boundsLine.geometry.dispose();
        this.boundsLine.geometry = new THREE.BufferGeometry().setFromPoints(cornersWorld);
        syncScreenSpaceDashedLineGeometry(this.boundsLine);

        this.handleSpriteMap.forEach((handleSprite, handleId) => {
            const uv = handleUvMap.get(handleId);
            if (!uv) {
                throw new Error(`缩放手柄 UV 缺失：${handleId}`);
            }
            const world = this.planeToWorld(uv, basis);
            handleSprite.position.copy(world);
            // 缩放控件控制点需要保持固定屏幕语义，避免随草图尺寸变化后出现重叠或过小。
            handleSprite.scale.set(
                SketchScaleController.HANDLE_CONTROL_POINT_SCALE,
                SketchScaleController.HANDLE_CONTROL_POINT_SCALE,
                SketchScaleController.HANDLE_CONTROL_POINT_SCALE,
            );
            handleSprite.visible = true;
        });
        this.refreshDebugOverlay(controlBounds, basis, boundsResult.sourceWorldPoints, handleUvMap);
        this.overlayRoot.visible = true;
        // 拖拽预览可能在单帧内多次刷新，普通请求可避免同步重复绘制。
        this.viewer.requestRender();
    }

    
    /**
     * 刷新缩放边界调试叠加层。
     *
     * @param bounds 当前二维边界。
     * @param basis 草图平面基。
     * @param sourceWorldPoints 参与边界计算的世界点。
     * @param handleUvMap 手柄 UV 映射表。
     */
    private refreshDebugOverlay(
        bounds: SketchBounds2D,
        basis: SketchPlaneBasis,
        sourceWorldPoints: THREE.Vector3[],
        handleUvMap: ReadonlyMap<SketchScaleHandleId, THREE.Vector2>,
    ): void {
        if (SketchScaleController.debug.overlayMode !== SketchScaleDebugOverlayMode.Bounds) {
            this.hideDebugOverlay();
            return;
        }
        if (!this.debugOverlayRoot || !this.debugBoundsLine || !this.debugSourcePoints || !this.debugHandlePoints) {
            return;
        }

        const cornersWorld = [
            this.planeToWorld(new THREE.Vector2(bounds.minX, bounds.minY), basis),
            this.planeToWorld(new THREE.Vector2(bounds.maxX, bounds.minY), basis),
            this.planeToWorld(new THREE.Vector2(bounds.maxX, bounds.maxY), basis),
            this.planeToWorld(new THREE.Vector2(bounds.minX, bounds.maxY), basis),
            this.planeToWorld(new THREE.Vector2(bounds.minX, bounds.minY), basis),
        ];
        this.debugBoundsLine.geometry.dispose();
        this.debugBoundsLine.geometry = new THREE.BufferGeometry().setFromPoints(cornersWorld);

        this.debugSourcePoints.geometry.dispose();
        this.debugSourcePoints.geometry = this.createDebugPointGeometry(sourceWorldPoints);

        const handleWorldPoints = Array.from(handleUvMap.values()).map((uv) => this.planeToWorld(uv, basis));
        this.debugHandlePoints.geometry.dispose();
        this.debugHandlePoints.geometry = new THREE.BufferGeometry().setFromPoints(handleWorldPoints);

        this.debugOverlayRoot.visible = true;
    }

    /**
     * 隐藏缩放边界调试叠加层。
     */
    private hideDebugOverlay(): void {
        if (this.debugOverlayRoot) {
            this.debugOverlayRoot.visible = false;
        }
    }

    /**
     * 创建调试点位几何。
     *
     * 设计说明：
     * 1. `sourceWorldPoints` 在调试叠加层内只读，避免可视化逻辑误改边界计算输入；
     * 2. Three.js `setFromPoints()` 类型声明要求可变数组，因此这里浅拷贝一份数组壳；
     * 3. 点对象本身不需要 clone，`setFromPoints()` 只读取坐标并写入 BufferAttribute。
     *
     * @param sourceWorldPoints 参与边界计算的世界点。
     * @returns 调试点位几何。
     */
    private createDebugPointGeometry(sourceWorldPoints: readonly THREE.Vector3[]): THREE.BufferGeometry {
        return new THREE.BufferGeometry().setFromPoints(Array.from(sourceWorldPoints));
    }
    

    /**
     * 开始缩放拖拽。
     * @param event 原始鼠标事件。
     */
    private beginScaleDrag(event: MouseEvent): void {
        if (event.button !== 0) {
            return;
        }
        if (!this.selectedSketchGroup) {
            return;
        }
        const hitHandleId = this.pickScaleHandle(event);
        if (!hitHandleId) {
            return;
        }

        const basis = this.getSketchPlaneBasis(this.selectedSketchGroup);
        const bounds = this.computeSketchBounds2D(this.selectedSketchGroup, basis);
        const geometryHandleUvMap = this.resolveHandleUvMap(bounds);
        const controlBounds = this.expandBoundsForScaleControls(bounds);
        const controlHandleUvMap = this.resolveHandleUvMap(controlBounds);
        const startHandleUv = controlHandleUvMap.get(hitHandleId);
        if (!startHandleUv) {
            throw new Error(`未找到起始手柄 UV：${hitHandleId}`);
        }
        const oppositeHandleId = this.resolveOppositeHandle(hitHandleId);
        const anchorUv = geometryHandleUvMap.get(oppositeHandleId);
        if (!anchorUv) {
            throw new Error(`未找到锚点手柄 UV：${oppositeHandleId}`);
        }
        const scaleAnchorUv = controlHandleUvMap.get(oppositeHandleId);
        if (!scaleAnchorUv) {
            throw new Error(`未找到倍率参考锚点 UV：${oppositeHandleId}`);
        }
        const pointSnapshots = this.collectPointSnapshots(this.selectedSketchGroup);
        if (pointSnapshots.some((snapshot) => snapshot.point.isFixed())) {
            throw new Error('草图存在固定点，禁止执行整体缩放。');
        }
        this.dragState = {
            sketchId: this.selectedSketchGroup.persistentId,
            handleId: hitHandleId,
            basis,
            anchorUv: anchorUv.clone(),
            scaleAnchorUv: scaleAnchorUv.clone(),
            startHandleUv: startHandleUv.clone(),
            pointSnapshots,
            sketchGroup: this.selectedSketchGroup,
        };

        this.viewer.setControlsEnabled(false);
        event.preventDefault();
        event.stopPropagation();
    }

    /**
     * 更新缩放拖拽。
     * @param event 原始鼠标事件。
     */
    private updateScaleDrag(event: MouseEvent): void {
        if (!this.dragState) {
            return;
        }
        const camera = this.viewer.camera;
        if (!camera || !this.viewer.container) {
            throw new Error('草图缩放拖拽更新失败：相机或容器不存在。');
        }

        this.updateMouseNdc(event.clientX, event.clientY);
        this.raycaster.setFromCamera(this.mouseNdc, camera);
        const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
            this.dragState.basis.normal,
            this.dragState.basis.origin,
        );
        const intersection = new THREE.Vector3();
        const hit = this.raycaster.ray.intersectPlane(plane, intersection);
        if (!hit) {
            throw new Error('草图缩放拖拽更新失败：鼠标射线未命中草图平面。');
        }
        const currentUv = this.worldToPlane(intersection, this.dragState.basis);
        const factor = this.resolveUniformScale(currentUv, this.dragState.scaleAnchorUv, this.dragState.startHandleUv);
        if (!this.applyScaleToSnapshots(this.dragState, factor)) {
            return;
        }
        this.refreshOverlay();
        event.preventDefault();
        event.stopPropagation();
    }

    /**
     * 结束缩放拖拽。
     */
    private endScaleDrag(): void {
        const finishedState = this.dragState;
        if (!finishedState) {
            return;
        }
        this.dragState = null;
        this.viewer.setControlsEnabled(true);
        this.setCursor(SketchScaleCursor.Default);

        const rollback = (): void => {
            this.rollbackSnapshots(finishedState);
            this.refreshOverlay();
        };
        try {
            /**
             * 缩放与移动、旋转属于同一种草图定义编辑。
             * 统一提交后，Feature 与转换实体引用协调器会消费同一个 committed 事件；
             * 禁止再从缩放控制器旁路调用 Feature 重建，否则会产生重复重建和时序分叉。
             * 这里仅回滚同步提交失败；提交后的异步派生重建失败由协调器记录诊断，
             * 不反向撤销已经确认的草图主状态。
             */
            finishedState.sketchGroup.commitDefinitionChanges({
                reason: SKETCH_SCALE_ENTITIES_COMMIT_REASON,
            });
            this.refreshOverlay();
        } catch (error) {
            rollback();
            throw error;
        }
    }

    /**
     * 鼠标悬停更新手柄游标。
     * @param event 原始鼠标事件。
     */
    private updateHandleHoverCursor(event: MouseEvent): void {
        if (!this.selectedSketchGroup || !this.overlayRoot?.visible) {
            this.setCursor(SketchScaleCursor.Default);
            return;
        }
        const handleId = this.pickScaleHandle(event);
        if (!handleId) {
            this.setCursor(SketchScaleCursor.Default);
            return;
        }
        this.setCursor(this.resolveCursorByHandle(handleId));
    }

    /**
     * 应用缩放到快照对象。
     * @param state 拖拽状态。
     * @param scale 等比缩放系数。
     */
    private applyScaleToSnapshots(state: SketchScaleDragState, scale: number): boolean {
        const targets = new Map<SketchPointObject, THREE.Vector3>();
        state.pointSnapshots.forEach((snapshot) => {
            targets.set(
                snapshot.point,
                this.transformWorldByScale(snapshot.world, state.basis, state.anchorUv, scale),
            );
        });

        /**
         * 圆、圆弧、椭圆、槽口与样条的非点参数均由实体结构方程派生，
         * 缩放控制器只提交全部定义点目标，不再手工写半径或调用点组 BFS。
         */
        return this.viewer.previewSketchParametricPointTargetsForSketch(
            state.sketchId,
            targets,
            `sketch-scale:${state.sketchId}`,
        );
    }

    /**
     * 回滚缩放快照。
     * @param state 拖拽状态。
     */
    private rollbackSnapshots(state: SketchScaleDragState): void {
        const targets = new Map<SketchPointObject, THREE.Vector3>();
        state.pointSnapshots.forEach((snapshot) => {
            targets.set(snapshot.point, snapshot.world.clone());
        });
        if (!this.viewer.previewSketchParametricPointTargetsForSketch(
            state.sketchId,
            targets,
            `sketch-scale:${state.sketchId}:rollback`,
        )) {
            throw new Error('草图缩放回滚失败：统一参数系统拒绝原始点位。');
        }
    }

    /**
     * 收集点快照。
     * @param sketchGroup 草图组。
     * @returns 点快照列表。
     */
    private collectPointSnapshots(sketchGroup: SketchGroup): SketchPointSnapshot[] {
        const pointMap = new Map<string, SketchPointObject>();
        sketchGroup.traverse((object) => {
            if (isSketchPointObject(object)) {
                pointMap.set(object.uuid, object);
            }
        });
        const snapshots = Array.from(pointMap.values()).map((point) => ({
            point,
            world: point.getPosition(),
        }));
        if (snapshots.length <= 0) {
            throw new Error('草图缩放失败：草图组中未找到可缩放点。');
        }
        return snapshots;
    }

    /**
     * 解析草图平面基。
     * @param sketchGroup 草图组。
     * @returns 平面基。
     */
    private getSketchPlaneBasis(sketchGroup: SketchGroup): SketchPlaneBasis {
        return {
            origin: sketchGroup.referencePlane.originWorld,
            xAxis: sketchGroup.referencePlane.xAxis,
            yAxis: sketchGroup.referencePlane.yAxis,
            normal: sketchGroup.referencePlane.normal.clone(),
        };
    }

    
    /**
     * 计算草图二维边界。
     * @param sketchGroup 草图组。
     * @param basis 草图平面基。
     * @returns 二维边界。
     */
    private computeSketchBounds2D(sketchGroup: SketchGroup, basis: SketchPlaneBasis): SketchBounds2D {
        return this.computeSketchBoundsCalculationResult(sketchGroup, basis).bounds;
    }

    /**
     * 计算草图二维边界与参与计算的世界点。
     * @param sketchGroup 草图组。
     * @param basis 草图平面基。
     * @returns 边界计算结果。
     */
    private computeSketchBoundsCalculationResult(
        sketchGroup: SketchGroup,
        basis: SketchPlaneBasis,
    ): SketchScaleBoundsCalculationResult {
        sketchGroup.updateWorldMatrix(true, true);
        const sourceWorldPoints = this.collectSketchBoundsWorldPoints(sketchGroup);
        if (sourceWorldPoints.length <= 0) {
            throw new Error('草图缩放失败：无法计算草图边界，草图顶点为空。');
        }

        let minX = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let minY = Number.POSITIVE_INFINITY;
        let maxY = Number.NEGATIVE_INFINITY;
        sourceWorldPoints.forEach((world) => {
            const uv = this.worldToPlane(world, basis);
            minX = Math.min(minX, uv.x);
            maxX = Math.max(maxX, uv.x);
            minY = Math.min(minY, uv.y);
            maxY = Math.max(maxY, uv.y);
        });

        if (!Number.isFinite(minX) || !Number.isFinite(maxX) || !Number.isFinite(minY) || !Number.isFinite(maxY)) {
            throw new Error('草图缩放失败：草图二维边界计算结果无效。');
        }
        if (Math.abs(maxX - minX) < 1e-8 || Math.abs(maxY - minY) < 1e-8) {
            throw new Error('草图缩放失败：草图边界退化，无法生成缩放手柄。');
        }
        return {
            bounds: {minX, maxX, minY, maxY},
            sourceWorldPoints,
        };
    }

    /**
     * 为缩放控制框外扩草图边界。
     *
     * 设计说明：
     * 1. 原始 bounds 仍表示草图真实几何范围；
     * 2. 控制框需要略微离开草图端点，避免手柄与草图点重叠；
     * 3. 外扩量只来自 bounds 对角线比例，不引入毫米级最小值，保持不同尺寸草图的相对一致性。
     *
     * @param bounds 原始草图二维边界。
     * @returns 外扩后的控制框边界。
     */
    private expandBoundsForScaleControls(bounds: SketchBounds2D): SketchBounds2D {
        const diagonal = Math.hypot(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY);
        const padding = diagonal * SketchScaleController.CONTROL_BOUNDS_PADDING_RATIO;
        return {
            minX: bounds.minX - padding,
            maxX: bounds.maxX + padding,
            minY: bounds.minY - padding,
            maxY: bounds.maxY + padding,
        };
    }

    /**
     * 收集参与草图缩放边界计算的世界点。
     *
     * 设计说明：
     * 1. 与框选保持一致，草图点只使用点中心，不使用 Sprite 显示四边形；
     * 2. 草图边只使用自身几何采样点，不把端点、控制点或集合子树混入边界；
     * 3. 草图边采样点在当前系统中已经是世界点，不能再次套用对象矩阵。
     *
     * @param sketchGroup 草图组。
     * @returns 参与边界计算的世界点列表。
     */
    private collectSketchBoundsWorldPoints(sketchGroup: SketchGroup): THREE.Vector3[] {
        const sourceWorldPoints: THREE.Vector3[] = [];
        sketchGroup.traverse((object) => {
            if (isSketchPointObject(object)) {
                this.collectSketchPointBoundsWorldPoint(object, sourceWorldPoints);
                return;
            }

            if (!isAbstractSketchEdgeObject(object)) {
                return;
            }

            this.collectSketchEdgeBoundsWorldPoints(object, sourceWorldPoints);
        });
        return sourceWorldPoints;
    }

    /**
     * 收集草图点的边界世界点。
     * @param point 草图点。
     * @param sourceWorldPoints 参与边界计算的世界点列表。
     */
    private collectSketchPointBoundsWorldPoint(
        point: SketchPointObject,
        sourceWorldPoints: THREE.Vector3[],
    ): void {
        if (!this.shouldUseSketchPointForBounds(point)) {
            return;
        }

        sourceWorldPoints.push(point.getPosition());
    }

    /**
     * 判断草图点是否应参与缩放边界计算。
     * @param point 草图点。
     * @returns 应参与边界计算时返回 true。
     */
    private shouldUseSketchPointForBounds(point: SketchPointObject): boolean {
        if (point.pointKind !== SketchPointKind.ControlPoint) {
            return true;
        }

        return point.boxSelectionRole === SketchPointBoxSelectionRole.RectangleConstructionIntersection;
    }

    /**
     * 收集草图边的边界世界点。
     * @param edge 草图边。
     * @param sourceWorldPoints 参与边界计算的世界点列表。
     */
    private collectSketchEdgeBoundsWorldPoints(
        edge: AbstractSketchEdgeObject,
        sourceWorldPoints: THREE.Vector3[],
    ): void {
        const position = edge.geometry.getAttribute('position');
        if (!position || position.count === 0) {
            return;
        }

        for (let index = 0; index < position.count; index += 1) {
            // 草图边几何采样点已是世界坐标，这里只复制值，避免重复应用对象矩阵导致边界偏移。
            sourceWorldPoints.push(new THREE.Vector3().fromBufferAttribute(position, index));
        }
    }
    

    /**
     * 解析 8 个手柄 UV 坐标。
     * @param bounds 二维边界。
     * @returns 手柄 UV 映射表。
     */
    private resolveHandleUvMap(bounds: SketchBounds2D): Map<SketchScaleHandleId, THREE.Vector2> {
        const centerX = (bounds.minX + bounds.maxX) * 0.5;
        const centerY = (bounds.minY + bounds.maxY) * 0.5;
        return new Map<SketchScaleHandleId, THREE.Vector2>([
            [SketchScaleHandleId.TopLeft, new THREE.Vector2(bounds.minX, bounds.maxY)],
            [SketchScaleHandleId.TopCenter, new THREE.Vector2(centerX, bounds.maxY)],
            [SketchScaleHandleId.TopRight, new THREE.Vector2(bounds.maxX, bounds.maxY)],
            [SketchScaleHandleId.MiddleLeft, new THREE.Vector2(bounds.minX, centerY)],
            [SketchScaleHandleId.MiddleRight, new THREE.Vector2(bounds.maxX, centerY)],
            [SketchScaleHandleId.BottomLeft, new THREE.Vector2(bounds.minX, bounds.minY)],
            [SketchScaleHandleId.BottomCenter, new THREE.Vector2(centerX, bounds.minY)],
            [SketchScaleHandleId.BottomRight, new THREE.Vector2(bounds.maxX, bounds.minY)],
        ]);
    }

    /**
     * 解析对侧锚点手柄。
     * @param handleId 当前手柄。
     * @returns 对侧手柄。
     */
    private resolveOppositeHandle(handleId: SketchScaleHandleId): SketchScaleHandleId {
        switch (handleId) {
            case SketchScaleHandleId.TopLeft:
                return SketchScaleHandleId.BottomRight;
            case SketchScaleHandleId.TopCenter:
                return SketchScaleHandleId.BottomCenter;
            case SketchScaleHandleId.TopRight:
                return SketchScaleHandleId.BottomLeft;
            case SketchScaleHandleId.MiddleLeft:
                return SketchScaleHandleId.MiddleRight;
            case SketchScaleHandleId.MiddleRight:
                return SketchScaleHandleId.MiddleLeft;
            case SketchScaleHandleId.BottomLeft:
                return SketchScaleHandleId.TopRight;
            case SketchScaleHandleId.BottomCenter:
                return SketchScaleHandleId.TopCenter;
            case SketchScaleHandleId.BottomRight:
                return SketchScaleHandleId.TopLeft;
            default:
                throw new Error(`未知缩放手柄：${handleId}`);
        }
    }

    /**
     * 根据手柄解析游标样式。
     * @param handleId 手柄 ID。
     * @returns 游标样式。
     */
    private resolveCursorByHandle(handleId: SketchScaleHandleId): SketchScaleCursor {
        if (handleId === SketchScaleHandleId.TopCenter || handleId === SketchScaleHandleId.BottomCenter) {
            return SketchScaleCursor.Vertical;
        }
        if (handleId === SketchScaleHandleId.MiddleLeft || handleId === SketchScaleHandleId.MiddleRight) {
            return SketchScaleCursor.Horizontal;
        }
        if (handleId === SketchScaleHandleId.TopLeft || handleId === SketchScaleHandleId.BottomRight) {
            return SketchScaleCursor.DiagonalNwse;
        }
        return SketchScaleCursor.DiagonalNesw;
    }

    /**
     * 计算等比缩放系数。
     * @param currentUv 当前鼠标 UV。
     * @param anchorUv 锚点 UV。
     * @param startHandleUv 起始手柄 UV。
     * @returns 缩放系数。
     */
    private resolveUniformScale(
        currentUv: THREE.Vector2,
        anchorUv: THREE.Vector2,
        startHandleUv: THREE.Vector2,
    ): number {
        const startDx = startHandleUv.x - anchorUv.x;
        const startDy = startHandleUv.y - anchorUv.y;
        const currentDx = currentUv.x - anchorUv.x;
        const currentDy = currentUv.y - anchorUv.y;
        const startLength = Math.hypot(startDx, startDy);
        if (startLength <= 1e-8) {
            throw new Error('草图缩放失败：起始手柄与锚点重合。');
        }
        const dirX = startDx / startLength;
        const dirY = startDy / startLength;
        const projected = currentDx * dirX + currentDy * dirY;
        const rawScale = projected / startLength;
        return THREE.MathUtils.clamp(rawScale, SketchScaleController.MIN_SCALE, SketchScaleController.MAX_SCALE);
    }

    /**
     * 缩放变换世界点。
     * @param world 原始世界点。
     * @param basis 平面基。
     * @param anchorUv 锚点 UV。
     * @param scale 缩放系数。
     * @returns 变换后世界点。
     */
    private transformWorldByScale(
        world: THREE.Vector3,
        basis: SketchPlaneBasis,
        anchorUv: THREE.Vector2,
        scale: number,
    ): THREE.Vector3 {
        const uv = this.worldToPlane(world, basis);
        const scaledUv = new THREE.Vector2(
            anchorUv.x + (uv.x - anchorUv.x) * scale,
            anchorUv.y + (uv.y - anchorUv.y) * scale,
        );
        return this.planeToWorld(scaledUv, basis);
    }

    /**
     * 世界点转换到草图平面 UV。
     * @param world 世界坐标。
     * @param basis 平面基。
     * @returns UV 坐标。
     */
    private worldToPlane(world: THREE.Vector3, basis: SketchPlaneBasis): THREE.Vector2 {
        const relative = world.clone().sub(basis.origin);
        return new THREE.Vector2(relative.dot(basis.xAxis), relative.dot(basis.yAxis));
    }

    /**
     * 草图平面 UV 转换到世界点。
     * @param uv UV 坐标。
     * @param basis 平面基。
     * @returns 世界坐标。
     */
    private planeToWorld(uv: THREE.Vector2, basis: SketchPlaneBasis): THREE.Vector3 {
        return basis.origin.clone()
            .addScaledVector(basis.xAxis, uv.x)
            .addScaledVector(basis.yAxis, uv.y);
    }

    /**
     * 更新鼠标 NDC。
     * @param clientX 鼠标 X。
     * @param clientY 鼠标 Y。
     */
    private updateMouseNdc(clientX: number, clientY: number): void {
        const container = this.viewer.container;
        if (!container) {
            throw new Error('草图缩放失败：容器不存在。');
        }
        const rect = container.getBoundingClientRect();
        this.mouseNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        this.mouseNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    }

    /**
     * 拾取缩放手柄。
     * @param event 原始鼠标事件。
     * @returns 命中的手柄 ID。
     */
    private pickScaleHandle(event: MouseEvent): SketchScaleHandleId | null {
        const camera = this.viewer.camera;
        const container = this.viewer.container;
        const entitySelectionManager = this.viewer.entitySelectionManager;
        if (!camera || !container || !entitySelectionManager) {
            throw new Error('草图缩放失败：相机、容器或选择管理器不存在。');
        }
        this.updateMouseNdc(event.clientX, event.clientY);
        this.raycaster.setFromCamera(this.mouseNdc, camera);
        const handleHits = entitySelectionManager.screenSpacePrecisionPicker.collectPointHits({
            points: Array.from(this.handleSpriteMap.values()),
            camera,
            containerRect: container.getBoundingClientRect(),
            clientX: event.clientX,
            clientY: event.clientY,
            ray: this.raycaster.ray,
        });
        if (handleHits.length <= 0) {
            return null;
        }
        const handleId = this.handleIdByObjectId.get(handleHits[0].object.uuid);
        if (!handleId) {
            throw new Error('草图缩放失败：命中对象未注册手柄映射。');
        }
        return handleId;
    }

    /**
     * 设置游标样式。
     * @param cursor 游标类型。
     */
    private setCursor(cursor: SketchScaleCursor): void {
        const container = this.viewer.container;
        if (!container) {
            throw new Error('草图缩放失败：容器不存在，无法设置游标。');
        }
        if (this.cursor === cursor) {
            return;
        }
        this.cursor = cursor;
        container.style.cursor = cursor;
    }
}
